import { extractVendorStatus } from "@/utils/helpers/vendorRedirection";
import type { ProjectsInArtworkListItem } from "@/services/artworkService";
import {
  getActiveAssessment,
  resolveVendorSelectionFromUrl,
} from "@/utils/productInfoDetailsSelectors";
import { convertUTCStringToLocal, formatToMMDDYYYY } from "@/utils/helper";

// Statuses that show the ArtworkInDesign component (case-insensitive comparison).
// Add new statuses here to allow them to render the design view.
export const ARTWORK_SHOW_DESIGN_STATUSES = new Set([
  'manually complete',
  'sku complete',
]);

export const isArtworkStatusAllowed = (status?: string): boolean => {
  if (!status) return false;
  return ARTWORK_SHOW_DESIGN_STATUSES.has(status.toString().trim().toLowerCase());
};

// Statuses that require redirect out of Artwork — vendors with these are not eligible for the Artwork tab.
const ARTWORK_REDIRECT_STATUSES = new Set([
  'IN PROGRESS',
  'VENDOR ASSESSMENT',
  'VENDOR APPROVED',
  'AWAITING BUSINESS REVIEW',
  'READY FOR TASTING PANEL',
  'TASTING PANEL APPROVED',
  'VENDOR INFORMATION REQUESTED',
  'PARTIALLY SUBMITTED',
  'UNDER PRODUCT DEVELOPER REVIEW',
  'UNDER INTERNAL REVIEW',
  'NEED MORE INFORMATION',
  'NEEDS REVISION',
]);

export const isVendorEligibleForArtwork = (vendor: any): boolean => {
  const status = extractVendorStatus(vendor, "").toUpperCase().replaceAll('_', ' ');
  return !ARTWORK_REDIRECT_STATUSES.has(status);
};

/**
 * `?vendorId=` may be either `id` or `vendorId` from the list / tab handlers.
 * Match the query against both so the status bar and tabs resolve the same row.
 */
export function vendorMatchesProjectsInArtworkUrlParam(
  v: any,
  urlVendorId: string | null | undefined
): boolean {
  if (v == null || urlVendorId == null) return false;
  const url = String(urlVendorId).trim();
  if (!url) return false;
  const idStr = v.id != null && String(v.id).trim() !== "" ? String(v.id) : "";
  const vendorIdStr =
    v.vendorId != null && String(v.vendorId).trim() !== "" ? String(v.vendorId) : "";
  return url === idStr || url === vendorIdStr;
}

/**
 * Same default / URL rules as Projects in Artwork vendor tabs:
 * Prefer `vendorAssessmentId` from URL when it matches a row, then `vendorId`,
 * else first `isVendorEligibleForArtwork` vendor, else index 0.
 */
export function getProjectsInArtworkActiveVendorIndex(
  vendors: any[],
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId?: string | null | undefined
): number {
  if (!Array.isArray(vendors) || vendors.length === 0) return 0;
  const aid = urlVendorAssessmentId != null ? String(urlVendorAssessmentId).trim() : "";
  if (aid) {
    const byAssessment = vendors.findIndex(
      (v: any) => String(v?.vendorAssessmentId ?? v?.assessmentId ?? "").trim() === aid
    );
    if (byAssessment >= 0 && isVendorEligibleForArtwork(vendors[byAssessment])) {
      return byAssessment;
    }
  }
  const url = urlVendorId != null ? String(urlVendorId).trim() : "";
  if (url) {
    const urlIdx = vendors.findIndex((v: any) => vendorMatchesProjectsInArtworkUrlParam(v, url));
    if (urlIdx >= 0 && isVendorEligibleForArtwork(vendors[urlIdx])) {
      return urlIdx;
    }
  }
  const firstEligibleIdx = vendors.findIndex(isVendorEligibleForArtwork);
  if (firstEligibleIdx >= 0) return firstEligibleIdx;
  return 0;
}

export function resolveProjectsInArtworkActiveVendor(
  vendors: any[],
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId?: string | null | undefined
): any | undefined {
  const idx = getProjectsInArtworkActiveVendorIndex(vendors, urlVendorId, urlVendorAssessmentId);
  return vendors[idx];
}

/** True when `vendorDetails` is product-info shape: groups with non-empty `vendorList`. */
export function vendorDetailsHasNestedVersionRows(vendorDetails: unknown): boolean {
  if (!Array.isArray(vendorDetails) || vendorDetails.length === 0) return false;
  return vendorDetails.some(
    (g: any) => Array.isArray(g?.vendorList) && g.vendorList.length > 0
  );
}

/** Merged assessment for status bar / hooks: nested product info + URL, or flat list row. */
export function resolveProjectsInArtworkActiveMergedVendor(
  product: any,
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId: string | null | undefined
): Record<string, unknown> | undefined {
  if (!product) return undefined;
  const vds = Array.isArray(product.vendorDetails) ? product.vendorDetails : [];
  if (vds.length === 0) return undefined;

  if (vendorDetailsHasNestedVersionRows(vds)) {
    const sel = resolveVendorSelectionFromUrl(product, {
      vendorId: urlVendorId ?? undefined,
      vendorAssessmentId: urlVendorAssessmentId ?? undefined,
    });
    if (sel?.merged) return sel.merged as Record<string, unknown>;
    const fb = getActiveAssessment(product, 0, 0);
    return (fb as Record<string, unknown>) ?? undefined;
  }

  const row = resolveProjectsInArtworkActiveVendor(vds, urlVendorId, urlVendorAssessmentId);
  return row as Record<string, unknown> | undefined;
}

/** UnifiedStatusBar keys: nested flattens version rows; flat uses top-level ids. */
export function buildArtworkDetailVendorStatusesMap(product: any): Record<string, any> {
  const out: Record<string, any> = {};
  const vds = Array.isArray(product?.vendorDetails) ? product.vendorDetails : [];
  if (vds.length === 0) return out;

  if (vendorDetailsHasNestedVersionRows(vds)) {
    vds.forEach((group: any) => {
      const list = Array.isArray(group?.vendorList) ? group.vendorList : [];
      list.forEach((row: any) => {
        const key = String(row?.assessmentId ?? row?.vendorAssessmentId ?? "").trim();
        if (key) {
          const status =
            row?.vendorAssessmentStatus ||
            row?.businessStatus ||
            group?.businessStatus ||
            "";
          out[key] = {
            status,
            ...(row?.workflowStatus ? { workflowStatus: row.workflowStatus } : {}),
          };
        }
      });
    });
    return out;
  }

  vds.forEach((v: any) => {
    const key = String(v?.vendorAssessmentId ?? v?.assessmentId ?? v?.id ?? v?.vendorId ?? "").trim();
    if (key) {
      const status = v?.vendorAssessmentStatus || v?.businessStatus || "";
      out[key] = {
        status,
        ...(v?.workflowStatus ? { workflowStatus: v.workflowStatus } : {}),
      };
    }
  });
  return out;
}

/** Status-config path check: merged row from nested product or flat `vendorDetails`. */
export function resolveArtworkVendorMergedForStatusPathCheck(
  product: any,
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId: string | null | undefined
): any | undefined {
  if (!product) return undefined;
  const vds = Array.isArray(product.vendorDetails) ? product.vendorDetails : [];
  if (vds.length === 0) return undefined;

  if (vendorDetailsHasNestedVersionRows(vds)) {
    const sel = resolveVendorSelectionFromUrl(product, {
      vendorId: urlVendorId ?? undefined,
      vendorAssessmentId: urlVendorAssessmentId ?? undefined,
    });
    if (sel?.merged) return sel.merged;
    return getActiveAssessment(product, 0, 0) ?? undefined;
  }

  return resolveArtworkVendorRowForStatusPathCheck(vds, urlVendorId, urlVendorAssessmentId);
}

/** Match URL assessment id to a vendor row without artwork eligibility filtering. */
export function findVendorDetailByVendorAssessmentId(
  vendors: any[],
  vendorAssessmentId: string | null | undefined
): any | undefined {
  if (!Array.isArray(vendors) || vendors.length === 0) return undefined;
  const aid = vendorAssessmentId != null ? String(vendorAssessmentId).trim() : "";
  if (!aid) return undefined;
  return vendors.find(
    (v: any) => String(v?.vendorAssessmentId ?? v?.assessmentId ?? "").trim() === aid
  );
}

/** Prefer explicit `businessStatus` on the vendor row (assessment-level) for workflow routing. */
export function getBusinessStatusForArtworkVendorRow(vendor: any): string {
  if (vendor == null || typeof vendor !== "object") return "";
  const direct = (vendor as { businessStatus?: unknown }).businessStatus;
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim();
  }
  return extractVendorStatus(vendor, "ARTWORK");
}

/** True when list/context vendor row has a non-empty artwork label (`artworkStatus` or mirrored `status`). */
export function vendorDetailHasArtworkStatus(vendor: unknown): boolean {
  if (vendor == null || typeof vendor !== "object") return false;
  const rec = vendor as Record<string, unknown>;
  const raw = String(rec.artworkStatus ?? rec.status ?? "").trim();
  return raw.length > 0;
}

/**
 * Vendor row for status vs URL checks: prefer exact `vendorAssessmentId` match from the URL,
 * then the same resolution as vendor tabs (eligible row / defaults).
 */
export function resolveArtworkVendorRowForStatusPathCheck(
  vendors: any[],
  urlVendorId: string | null | undefined,
  urlVendorAssessmentId: string | null | undefined
): any | undefined {
  if (!Array.isArray(vendors) || vendors.length === 0) return undefined;
  const aid = urlVendorAssessmentId != null ? String(urlVendorAssessmentId).trim() : "";
  if (aid) {
    const found = findVendorDetailByVendorAssessmentId(vendors, aid);
    if (found) return found;
  }
  return resolveProjectsInArtworkActiveVendor(vendors, urlVendorId, aid || undefined);
}

export function formatArtworkProjectIdDisplay(raw: unknown): string {
  if (raw == null) return "-";
  const s = String(raw).trim();
  return s || "-";
}

export type ArtworkProjectIdPair = {
  id: string;
  label: string;
  artworkProjectId: string | null;
  vendorAssessmentId?: string;
  vendorAccountId?: string;
};

export function getArtworkProjectIdPairsFromRow(row: ProjectsInArtworkListItem, versionIndex: number = 0): ArtworkProjectIdPair[] {
  const fromFlatDetails =
    Array.isArray(row?.vendorDetails) && (row.vendorDetails as unknown[]).length > 0;
  const vendorRows = fromFlatDetails
    ? (row.vendorDetails as unknown[])
    : ((row?.vendorList as { objects?: unknown } | undefined)?.objects as unknown[]) || [];

  return (Array.isArray(vendorRows) ? vendorRows : [])
    .map((v: any) => {
      const vaId =
        v?.vendorAssessmentId != null && String(v.vendorAssessmentId).trim() !== ""
          ? String(v.vendorAssessmentId).trim()
          : "";
      const accountId =
        v?.vendorId != null && String(v.vendorId).trim() !== ""
          ? String(v.vendorId).trim()
          : v?.id != null && String(v.id).trim() !== ""
            ? String(v.id).trim()
            : "";
      const pid =
        v?.artworkProjectId != null && String(v.artworkProjectId).trim() !== ""
          ? String(v.artworkProjectId).trim()
          : v?.projectId != null && String(v.projectId).trim() !== ""
            ? String(v.projectId).trim()
            : null;
      const vn = String(v?.versionName ?? "").trim();
      const baseLabel = String(v?.vendorCorporateName ?? v?.vendorName ?? "").trim();
      const label = vn && baseLabel ? `${baseLabel} (${vn})` : baseLabel || vn;
      return {
        id: vaId || accountId,
        label,
        artworkProjectId: pid,
        vendorAssessmentId: vaId || undefined,
        vendorAccountId: accountId || undefined,
      };
    })
    .filter((p: ArtworkProjectIdPair) => Boolean(p.label));
}

/** Plain-text summary for grid export / sorting; matches {@link getArtworkProjectIdColumnRenderParts} `primaryText`. */
export function getArtworkProjectIdColumnDisplayText(row: ProjectsInArtworkListItem): string {
  return getArtworkProjectIdColumnRenderParts(row).primaryText;
}

export type ArtworkProjectIdColumnRenderParts = {
  primaryText: string;
  pairs: ArtworkProjectIdPair[];
  hasAnyVendorMultiVersion: boolean;
};

/**
 * List column: single version → project id; multiple versions → `"{firstProjectId} · {n}"` (no `+`; `+` is vendor-column only).
 */
export function getArtworkProjectIdColumnRenderParts(row: ProjectsInArtworkListItem): ArtworkProjectIdColumnRenderParts {
  const pairs = getArtworkProjectIdPairsFromRow(row, 0);
  const hasAnyVendorMultiVersion = Boolean(row.artworkListMeta?.hasAnyVendorMultiVersion);
  if (pairs.length === 0) {
    return { primaryText: "-", pairs, hasAnyVendorMultiVersion };
  }
  if (pairs.length === 1) {
    return {
      primaryText: formatArtworkProjectIdDisplay(pairs[0]?.artworkProjectId),
      pairs,
      hasAnyVendorMultiVersion,
    };
  }
  const n = pairs.length;
  const firstPid =
    pairs.find((p) => p.artworkProjectId != null && String(p.artworkProjectId).trim() !== "")?.artworkProjectId ??
    null;
  const pidDisp = formatArtworkProjectIdDisplay(firstPid);
  const primaryText = pidDisp !== "-" ? `${pidDisp} · ${n}` : `${n}`;
  return { primaryText, pairs, hasAnyVendorMultiVersion };
}

/** Date part for list / tooltip (matches legacy artwork deadline column). */
export function formatVendorArtworkDeadlineDisplay(raw: unknown): string {

  if (raw == null) return "-";
  if (typeof raw === "object") return "-";
  const s = String(raw).trim();
  if (s === "") return "-";
  const datePart = formatToMMDDYYYY(convertUTCStringToLocal(s));
  return datePart || "-";
}

export type ArtworkDeadlinePair = {
  id: string;
  label: string;
  /** Formatted date part or "-" */
  deadlineDisplay: string;
  vendorAssessmentId?: string;
  vendorAccountId?: string;
};

/** When no deadline pairs (e.g. missing labels), prefer vendor-level `artWorkDeadline` then row aggregate. */
export function getArtworkDeadlineFallbackRaw(row: ProjectsInArtworkListItem): unknown {
  const details = Array.isArray(row?.vendorDetails) ? row.vendorDetails : [];
  for (const v of details) {
    const raw = (v as { artWorkDeadline?: unknown; artworkDeadline?: unknown })?.artWorkDeadline ??
      (v as { artworkDeadline?: unknown })?.artworkDeadline;
    if (raw != null && String(raw).trim() !== "") return raw;
  }
  const objs = (row?.vendorList as { objects?: unknown[] } | undefined)?.objects;
  if (Array.isArray(objs)) {
    for (const v of objs) {
      const rec = v as { artWorkDeadline?: unknown; artworkDeadline?: unknown };
      const raw = rec?.artWorkDeadline ?? rec?.artworkDeadline;
      if (raw != null && String(raw).trim() !== "") return raw;
    }
  }
  return (row as { artworkDeadline?: unknown; artWorkDeadlines?: unknown })?.artworkDeadline ??
    (row as { artWorkDeadlines?: unknown })?.artWorkDeadlines;
}

export function getArtworkDeadlinePairsFromRow(row: ProjectsInArtworkListItem, versionIndex: number = 0): ArtworkDeadlinePair[] {
  const fromFlatDetails =
    Array.isArray(row?.vendorDetails) && (row.vendorDetails as unknown[]).length > 0;
  const vendorRows = fromFlatDetails
    ? (row.vendorDetails as unknown[])
    : ((row?.vendorList as { objects?: unknown } | undefined)?.objects as unknown[]) || [];

  return (Array.isArray(vendorRows) ? vendorRows : [])
    .map((v: any) => {
      const raw = v?.artWorkDeadline ?? v?.artworkDeadline ?? null;
      const vaId =
        v?.vendorAssessmentId != null && String(v.vendorAssessmentId).trim() !== ""
          ? String(v.vendorAssessmentId).trim()
          : "";
      const accountId =
        v?.vendorId != null && String(v.vendorId).trim() !== ""
          ? String(v.vendorId).trim()
          : v?.id != null && String(v.id).trim() !== ""
            ? String(v.id).trim()
            : "";
      const vn = String(v?.versionName ?? "").trim();
      const baseLabel = String(v?.vendorCorporateName ?? v?.vendorName ?? "").trim();
      const label = vn && baseLabel ? `${baseLabel} (${vn})` : baseLabel || vn;
      return {
        id: vaId || accountId,
        label,
        deadlineDisplay: formatVendorArtworkDeadlineDisplay(raw),
        vendorAssessmentId: vaId || undefined,
        vendorAccountId: accountId || undefined,
      };
    })
    .filter((p: ArtworkDeadlinePair) => Boolean(p.label));
}
